import { Block, isPrestation } from '../types';

export class BlockCalculationService {
  // Calculer le total d'un chiffrage
  static calculateTotal(blocks: Block[]): number {
    return blocks.reduce((total, block) => {
      // Tous les blocs (sections et prestations) peuvent avoir un prixTotal
      if (block.content.prixTotal && block.content.prixTotal.trim() !== '') {
        return total + this.parsePrice(block.content.prixTotal);
      }
      
      // Pour les prestations uniquement, calculer prixUnitaire * quantite si pas de prixTotal
      if (isPrestation(block)) {
        const prixUnitaire = this.parsePrice(block.content.prixUnitaire || '0');
        const quantite = this.parseQuantity(block.content.quantite || '1');
        return total + (prixUnitaire * quantite);
      }
      
      return total;
    }, 0);
  }

  // Calculer le reste du budget
  static calculateRemainingBudget(budgetTotal: string, totalChiffrage: number): number {
    const budget = this.parsePrice(budgetTotal);
    return budget - totalChiffrage;
  }

  // Parser un prix (supporte les formats français)
  static parsePrice(price: string): number {
    return parseFloat(price.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
  }

  // Parser une quantité
  static parseQuantity(quantity: string): number {
    return parseFloat(quantity.replace(/[^\d.,]/g, '').replace(',', '.')) || 1;
  }

  // Formater un prix pour l'affichage
  static formatPrice(price: number): string {
    return Math.round(price).toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  // Calculer le prix total d'un bloc (prestation ou section)
  static calculatePrestationTotal(block: Block, allBlocks?: Block[]): number {
    // Utiliser le prixTotal s'il existe
    if (block.content.prixTotal && block.content.prixTotal.trim() !== '') {
      return this.parsePrice(block.content.prixTotal);
    }
    
    // Pour les prestations uniquement, calculer prixUnitaire * quantite si pas de prixTotal
    if (isPrestation(block)) {
      const prixUnitaire = this.parsePrice(block.content.prixUnitaire || '0');
      const quantite = this.parseQuantity(block.content.quantite || '1');
      return prixUnitaire * quantite;
    }
    
    // Pour les sections, calculer la somme des enfants si allBlocks est fourni
    if (!isPrestation(block) && allBlocks && block.children.length > 0) {
      const childBlocks = block.children
        .map(childId => allBlocks.find(b => b.id === childId))
        .filter(Boolean) as Block[];
      
      return childBlocks.reduce((total, childBlock) => {
        return total + this.calculatePrestationTotal(childBlock, allBlocks);
      }, 0);
    }
    
    // Pour les sections sans enfants ou sans allBlocks, retourner 0
    return 0;
  }

  // Mettre à jour le prix total d'un bloc (prestation ou section)
  static updatePrestationTotal(block: Block, allBlocks?: Block[]): Block {
    const prixTotal = this.calculatePrestationTotal(block, allBlocks);
    
    return {
      ...block,
      content: {
        ...block.content,
        prixTotal: this.formatPrice(prixTotal)
      }
    };
  }

  // Calculer les statistiques d'un projet
  static calculateProjectStats(blocks: Block[]) {
    const prestations = blocks.filter(isPrestation);
    const sections = blocks.filter(block => !isPrestation(block));
    
    const totalChiffrage = this.calculateTotal(blocks);
    const nombrePrestations = prestations.length;
    const nombreSections = sections.length;
    
    // Calculer la moyenne des prix unitaires
    const prixUnitaires = prestations
      .map(p => this.parsePrice(p.content.prixUnitaire || '0'))
      .filter(p => p > 0);
    
    const prixMoyen = prixUnitaires.length > 0 
      ? prixUnitaires.reduce((sum, prix) => sum + prix, 0) / prixUnitaires.length
      : 0;

    return {
      totalChiffrage,
      nombrePrestations,
      nombreSections,
      prixMoyen: this.formatPrice(prixMoyen)
    };
  }
}
